/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './style.module.scss';
import { Actionable, Text, View } from '@az/starc-ui';
import { Rating } from './Rating/Rating';
import { Author } from './Author/Author';
import { Content } from './Content/Content';
import { PurchasedFor } from './PurchasedFor/PurchasedFor';
import { Syndicated } from './Badges/Syndicated';
import { Response } from './Response/Response';
import { SecondaryRatingsList } from './SecondaryRating/SecondaryRatingsList';
import { ReviewFeedbackActions } from '@/features/reviews/components/ReviewFeedbackActions/ReviewFeedbackActions';
import { useState } from 'react';
import { VerifiedPurchase } from './Badges/VerifiedPurchase';
import { Recommended } from './Badges/Recommended';
import { Sweepstakes } from './Badges/Sweepstakes';
import type { ReviewType, ReviewMediaItem } from '../../interface/types';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { findBadgeById } from '../../utils/findBadgeById';
import NextImage from '@/components/NextImage';
import { PlayButton } from '@/components/PlayButton/PlayButton';
import React from 'react';
import { ReviewMediaDetailsModal } from '../ReviewImages/ReviewImageDetailsModal/ReviewMediaDetailsModal';
import { extractMediaItems } from '../../utils/extractMediaItems';
import { useLabels } from '@/hooks/useLabels';

const labelMap = {
  video: 'label_video',
  image: 'label_image',
};

type ReviewProps = {
  review: ReviewType;
  detailsModal?: boolean;
  selectedKeyword?: string;
};

type ImageProps = {
  mediaItems: ReviewMediaItem[];
  reviews?: ReviewType[] | null | undefined | ReviewType;
  renderReview: (reviewData: ReviewType, detailsModal: boolean) => React.ReactNode;
};

const ReviewMedia = ({ mediaItems, reviews, renderReview }: ImageProps) => {
  const labels = useLabels(labelMap);

  const [isDetailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const handleDetailsModalState: React.Dispatch<React.SetStateAction<boolean>> = (data) => {
    setDetailsModalOpen(data);
  };
  return (
    <View className={styles.imagesContainer}>
      {mediaItems.map((media, index) => {
        const thumbnailUrl = media.type === 'photo' ? media.url : media.thumbnailUrl;
        const stableKey = media.type === 'photo' ? media.url : media.videoUrl || `media-${index}`;
        const mediaTypeLabel = media.type === 'photo' ? labels.image : labels.video;
        return (
          <Actionable
            key={stableKey}
            onClick={() => {
              setSelectedImageIndex(index);
              setDetailsModalOpen(true);
            }}
          >
            <div className={styles.thumbnailWrapper}>
              <NextImage
                src={thumbnailUrl}
                alt={media.caption || `${mediaTypeLabel} #${index + 1}`}
                width={100}
                height={100}
                className={styles.images}
              />
              {media.type === 'video' && (
                <div className={styles.playButtonOverlay}>
                  <PlayButton />
                </div>
              )}
            </div>
          </Actionable>
        );
      })}
      {isDetailsModalOpen && (
        <ReviewMediaDetailsModal
          isReviewDetailsModalOpen={isDetailsModalOpen}
          handleReviewDetailsModalOpen={handleDetailsModalState}
          mediaItems={mediaItems}
          imageIndex={selectedImageIndex}
          setImageIndex={setSelectedImageIndex}
          reviews={reviews}
          renderReview={renderReview}
        />
      )}
    </View>
  );
};

export const Review = function Review({ review, detailsModal, selectedKeyword }: ReviewProps) {
  const {
    reviewId,
    reviewRating,
    reviewTitle,
    reviewerNickname,
    reviewText,
    reviewerVehicle,
    recommendedFlag,
    totalNegativeFeedbackCount,
    totalPositiveFeedbackCount,
    reviewSubmissionTimestamp,
    reviewPhotos,
    reviewVideos,
    reviewResponseText,
    reviewResponseTeamName,
    reviewResponseSubmissionTimestamp,
    reviewSecondaryRatings,
    reviewBadges,
  } = review;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));

  const mediaItems = extractMediaItems(reviewPhotos, reviewVideos);

  const showPreviewMedia = !detailsModal && mediaItems.length > 0;

  const verifiedPurchaseBadge = findBadgeById(reviewBadges, 'verifiedPurchaser');

  const sweepstakesBadge = findBadgeById(reviewBadges, 'incentivizedReview');

  const syndicatedBadge = findBadgeById(reviewBadges, 'Syndicated');

  const hasBadges = Boolean(verifiedPurchaseBadge || recommendedFlag || sweepstakesBadge);

  const hasResponseRequiredFields =
    reviewResponseText && reviewResponseTeamName && reviewResponseSubmissionTimestamp;

  const renderReview = (reviewData: ReviewType, detailsModal: boolean) => {
    return <Review review={reviewData} detailsModal={detailsModal} />;
  };

  return (
    <View className={styles.reviewContainer}>
      <View className={styles.reviewHeader}>
        <Rating stars={reviewRating} />
        {reviewTitle ? (
          <Text as="h3" variant="main-body" weight="bold" className={styles.title}>
            {reviewTitle}
          </Text>
        ) : null}
        <Author date={reviewSubmissionTimestamp} nickname={reviewerNickname} />
        {hasBadges ? (
          <View className={styles.badgesContainer}>
            {verifiedPurchaseBadge ? <VerifiedPurchase /> : null}
            {recommendedFlag ? <Recommended /> : null}
            {sweepstakesBadge ? <Sweepstakes /> : null}
          </View>
        ) : null}
      </View>

      {showPreviewMedia && isMobile ? (
        <ReviewMedia mediaItems={mediaItems} reviews={review} renderReview={renderReview} />
      ) : null}
      {reviewText ? (
        <Content text={reviewText} detailsModal={detailsModal} selectedKeyword={selectedKeyword} />
      ) : null}
      {showPreviewMedia && !isMobile ? (
        <ReviewMedia mediaItems={mediaItems} reviews={review} renderReview={renderReview} />
      ) : null}
      <View className={styles.reviewDetails}>
        {reviewerVehicle ? <PurchasedFor vehicle={reviewerVehicle} /> : null}
        {!syndicatedBadge ? (
          <ReviewFeedbackActions
            reviewId={reviewId}
            positiveFeedbackCount={totalPositiveFeedbackCount}
            negativeFeedbackCount={totalNegativeFeedbackCount}
          />
        ) : null}
      </View>
      {!detailsModal && reviewSecondaryRatings.length > 0 ? (
        <SecondaryRatingsList list={reviewSecondaryRatings} />
      ) : null}
      {syndicatedBadge ? <Syndicated {...syndicatedBadge} /> : null}
      {hasResponseRequiredFields ? (
        <Response
          date={reviewResponseSubmissionTimestamp}
          team={reviewResponseTeamName}
          text={reviewResponseText}
        />
      ) : null}
    </View>
  );
};
